<template>
  <div
    class="v3-form behaviour-delete-core-value-form"
    data-testid="behaviour-delete-core-value-form"
  >
    <p class="behaviour-delete-core-value-form__message text-muted mb-3">
      {{
        $t("behaviour_settings.delete_core_value_message", {
          name: coreValueName,
          coreValue: behaviourTerminology.coreValue
        })
      }}
    </p>

    <div v-if="!hasTransferTargets" class="mb-3">
      <InfoBox class="mb-0" role="status">
        {{
          $t("behaviour_settings.delete_core_value_no_transfer_targets", {
            coreValue: behaviourTerminology.coreValue
          })
        }}
      </InfoBox>
    </div>

    <div v-else class="mb-3">
      <Label
        required
        label-id="behaviour-cv-transfer-lbl"
        html-for="behaviour-cv-transfer-input"
      >
        {{
          $t("behaviour_settings.delete_core_value_transfer_label", {
            coreValue: behaviourTerminology.coreValue
          })
        }}
      </Label>
      <BehaviourCoreValueLazySelect
        :model-value="state.transferCoreValueId"
        class="w-100"
        hide-floating-label
        labelled-by="behaviour-cv-transfer-lbl"
        :placeholder="
          $t('behaviour_settings.delete_core_value_transfer_placeholder', {
            coreValue: behaviourTerminology.coreValue
          })
        "
        :excluded-ids="excludedTransferIds"
        :invalid-feedback="errors.transferCoreValueId"
        input-id="behaviour-cv-transfer-input"
        append-dropdown-to-body
        append-dropdown-fixed-position
        data-testid="behaviour-cv-transfer-select"
        @update:model-value="(v) => (state.transferCoreValueId = v)"
      />
    </div>

    <InfoBox
      v-if="state.serverMessage"
      class="mb-3"
      :class="{ danger: state.isServerError }"
      :title="state.serverTitle"
      role="status"
      aria-live="polite"
    >
      {{ state.serverMessage }}
    </InfoBox>

    <div class="text-center">
      <LoadingButton
        class="me-2 px-4"
        type="button"
        rounded
        data-testid="behaviour-cv-delete-cancel"
        @click="onCancel"
      >
        {{ $t("common.cancel") }}
      </LoadingButton>
      <LoadingButton
        class="px-4 behaviour-delete-core-value-form__delete-btn"
        type="button"
        solid
        rounded
        :is-loading="state.isProcessing"
        :disabled="state.isProcessing || !canDelete"
        data-testid="behaviour-cv-delete-confirm"
        @click="onDelete"
      >
        {{ $t("behaviour_settings.delete_core_value_confirm") }}
      </LoadingButton>
    </div>
  </div>
</template>

<script>
import { parseCoreValueApiError } from "@/helpers/behaviourCoreValueForm.js"
import BehaviourCoreValueLazySelect from "@/v3components/Behaviour/BehaviourCoreValueLazySelect.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import { computed, inject, nextTick, reactive } from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

export default {
  name: "BehaviourDeleteCoreValueForm",
  components: {
    BehaviourCoreValueLazySelect,
    InfoBox,
    Label,
    LoadingButton
  },
  props: {
    coreValue: {
      type: Object,
      required: true
    },
    transferTargets: {
      type: Array,
      default: () => []
    }
  },
  emits: ["cancel"],
  setup(props, { emit }) {
    const { t } = useI18n()
    const store = useStore()
    const modal = inject("modal")

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"]
    )

    const coreValueName = computed(() => props.coreValue?.name || "")

    const hasTransferTargets = computed(
      () => (props.transferTargets || []).length > 0
    )

    const excludedTransferIds = computed(() =>
      props.coreValue?.id != null ? [props.coreValue.id] : []
    )

    /**
     * Allow deletion even when no transfer targets exist; the
     * `replacement_core_value_id` payload field is optional per the API spec.
     */
    const canDelete = computed(() => true)

    const state = reactive({
      transferCoreValueId: null,
      isProcessing: false,
      serverTitle: "",
      serverMessage: "",
      isServerError: false,
      touched: false
    })

    const errors = computed(() => {
      if (!state.touched || !hasTransferTargets.value) return {}
      if (
        state.transferCoreValueId != null &&
        state.transferCoreValueId !== ""
      ) {
        return {}
      }
      return {
        transferCoreValueId: t(
          "behaviour_settings.delete_core_value_transfer_required",
          { coreValue: behaviourTerminology.value.coreValue }
        )
      }
    })

    const onCancel = () => {
      emit("cancel")
    }

    const onDelete = async () => {
      state.touched = true
      if (!canDelete.value) return
      if (Object.keys(errors.value).length) return

      try {
        state.isProcessing = true
        state.isServerError = false
        state.serverMessage = ""
        state.serverTitle = ""

        await store.dispatch("behaviourSettings/deleteCoreValue", {
          id: props.coreValue?.id,
          replacementCoreValueId: state.transferCoreValueId
        })

        modal?.setAction?.("coreValueDeleted", {
          id: props.coreValue?.id,
          transferCoreValueId: state.transferCoreValueId
        })
        // Let the parent watcher observe `action`/`actionData` before
        // `modal.close()` resets `modal.options`.
        await nextTick()
        emit("cancel")
      } catch (error) {
        const parsed = parseCoreValueApiError(error)
        state.isServerError = true
        state.serverTitle = t("behaviour_settings.core_value_error_title")
        state.serverMessage =
          parsed?.message ||
          t("behaviour_settings.delete_core_value_error", {
            coreValue: behaviourTerminology.value.coreValue
          })
      } finally {
        state.isProcessing = false
      }
    }

    return {
      behaviourTerminology,
      coreValueName,
      hasTransferTargets,
      excludedTransferIds,
      canDelete,
      state,
      errors,
      onCancel,
      onDelete
    }
  }
}
</script>
